<script setup>
import { computed } from 'vue'
import { useFormSize } from 'element-plus'

const props = defineProps({
  id: {
    type: [Number, String],
    required: true
  },
  name: {
    type: String,
    default: ''
  },
  closable: {
    type: Boolean,
    default: true
  },
  disabled: {
    type: Boolean,
    default: false
  }
})
const emit = defineEmits(['close'])

const formSize = useFormSize()
const sizeClass = computed(() => {
  const s = formSize.value || 'default'
  if (s === 'small') return 'is-small'
  if (s === 'large') return 'is-large'
  return 'is-default'
})

const displayName = computed(() => props.name || String(props.id))

const onClose = () => {
  emit('close', props.id)
}
</script>

<template>
  <span class="dept-tag" :class="[sizeClass, { 'is-disabled': disabled }]" :title="displayName">
    <span class="dept-tag-icon" aria-hidden="true">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 21h18M5 21V7a2 2 0 012-2h10a2 2 0 012 2v14M9 9h1m4 0h1M9 13h1m4 0h1M10 21v-4a1 1 0 011-1h2a1 1 0 011 1v4" />
      </svg>
    </span>
    <span class="dept-tag-name">{{ displayName }}</span>
    <button
      v-if="closable && !disabled"
      type="button"
      class="dept-tag-close"
      aria-label="移除"
      @click="onClose"
    >
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M6 18L18 6M6 6l12 12" />
      </svg>
    </button>
  </span>
</template>

<style scoped lang="scss">
.dept-tag {
  --tag-h: 30px;
  --tag-pad-x: 4px;
  --tag-gap: 6px;
  --tag-icon: 22px;
  --tag-font: 13px;

  display: inline-flex;
  align-items: center;
  gap: var(--tag-gap);
  max-width: 100%;
  height: var(--tag-h);
  padding: 0 var(--tag-pad-x);
  border: 1px solid #dbeafe;
  border-radius: 9999px;
  background-color: #eff6ff;
  color: #1d4ed8;
  font-size: var(--tag-font);
  line-height: 1;
  transition: background-color 0.16s ease, border-color 0.16s ease, color 0.16s ease, opacity 0.16s ease;

  &:hover {
    border-color: #bfdbfe;
    background-color: #dbeafe;
  }

  &.is-small {
    --tag-h: 24px;
    --tag-pad-x: 3px;
    --tag-gap: 4px;
    --tag-icon: 18px;
    --tag-font: 12px;
  }

  &.is-large {
    --tag-h: 34px;
    --tag-pad-x: 6px;
    --tag-gap: 8px;
    --tag-icon: 26px;
    --tag-font: 14px;
  }

  &.is-disabled {
    background-color: #f1f5f9;
    border-color: #e2e8f0;
    color: #94a3b8;
    cursor: not-allowed;
    opacity: 0.85;
  }
}

.dept-tag-icon {
  flex: 0 0 var(--tag-icon);
  width: var(--tag-icon);
  height: var(--tag-icon);
  border-radius: 9999px;
  background: linear-gradient(135deg, #bfdbfe 0%, #93c5fd 100%);
  color: #1d4ed8;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  svg {
    width: calc(var(--tag-icon) * 0.58);
    height: calc(var(--tag-icon) * 0.58);
  }
}

.dept-tag-name {
  flex: 1 1 auto;
  min-width: 0;
  max-width: 140px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-weight: 500;
}

.dept-tag.is-small .dept-tag-name {
  max-width: 100px;
}

.dept-tag.is-large .dept-tag-name {
  max-width: 180px;
}

.dept-tag-close {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  padding: 0;
  margin-right: 2px;
  border: 0;
  border-radius: 9999px;
  background-color: transparent;
  color: currentColor;
  opacity: 0.55;
  cursor: pointer;
  transition: opacity 0.16s ease, background-color 0.16s ease;

  svg {
    width: 10px;
    height: 10px;
  }

  &:hover {
    opacity: 1;
    background-color: rgba(37, 99, 235, 0.15);
  }
}

.dept-tag.is-small .dept-tag-close {
  width: 14px;
  height: 14px;

  svg {
    width: 9px;
    height: 9px;
  }
}
</style>
